const BASE_API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const VIETMAP_TILE_KEY =
  process.env.REACT_APP_VIETMAP_TILE_KEY ??
  process.env.REACT_APP_VIETMAP_API_KEY ??
  "";

export const hasVietmapTileKey = VIETMAP_TILE_KEY.trim().length > 0;
// Service key is handled in backend proxy and should never be exposed in frontend.
export const hasVietmapServiceKey = true;
export const hasVietmapApiKey = hasVietmapTileKey && hasVietmapServiceKey;
export const VIETMAP_STYLE_URL = hasVietmapTileKey
  ? `https://maps.vietmap.vn/maps/styles/tm/style.json?apikey=${VIETMAP_TILE_KEY}`
  : "";

export interface VietmapAddressSuggestion {
  id: string;
  refId: string;
  display: string;
  address: string;
  name: string;
}

export interface VietmapPlaceDetail {
  display: string;
  address: string;
  name: string;
  lat: number;
  lng: number;
}

interface SearchSuggestionsOptions {
  signal?: AbortSignal;
  focus?: {
    latitude: number;
    longitude: number;
  };
  limit?: number;
}

type JsonRecord = Record<string, unknown>;

const isJsonRecord = (value: unknown): value is JsonRecord =>
  typeof value === "object" && value !== null;

const asString = (value: unknown) => (typeof value === "string" ? value : "");
const asNumber = (value: unknown) =>
  typeof value === "number" ? value : Number.NaN;

const getAuthHeader = (): Record<string, string> => {
  const token = sessionStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Errors carry i18n keys (callers render them with t()); backend messages pass through as-is.
const parseProxyError = async (response: Response) => {
  if (response.status === 401) {
    throw new Error("venue.sessionExpired");
  }

  let message = "venue.requestFailed";
  try {
    const payload: unknown = await response.json();
    if (isJsonRecord(payload)) {
      if (typeof payload.message === "string" && payload.message.trim()) {
        message = payload.message;
      } else if (typeof payload.detail === "string" && payload.detail.trim()) {
        message = payload.detail;
      }
    }
  } catch {
    // ignore JSON parse failures and use default message
  }

  throw new Error(message);
};

export const searchVietmapAddressSuggestions = async (
  keyword: string,
  options: SearchSuggestionsOptions = {}
): Promise<VietmapAddressSuggestion[]> => {
  const query = keyword.trim();
  if (query.length < 2) return [];

  const url = new URL(`${BASE_API_URL}/vietmap/autocomplete`);
  url.searchParams.set("text", query);
  url.searchParams.set("limit", String(options.limit ?? 6));

  if (options.focus) {
    url.searchParams.set(
      "focus",
      `${options.focus.latitude.toFixed(6)},${options.focus.longitude.toFixed(
        6
      )}`
    );
  }

  const response = await fetch(url.toString(), {
    signal: options.signal,
    headers: {
      ...getAuthHeader(),
    },
  });
  if (!response.ok) {
    await parseProxyError(response);
  }

  const payload: unknown = await response.json();
  if (!Array.isArray(payload)) return [];

  return payload.flatMap((item) => {
    if (!isJsonRecord(item)) return [];

    const refId = asString(item.ref_id);
    const display = asString(item.display);
    const address = asString(item.address);
    const name = asString(item.name);
    const label = display || [name, address].filter(Boolean).join(" ").trim();

    if (!refId || !label) return [];

    return [
      {
        id: refId,
        refId,
        display: label,
        address,
        name,
      },
    ];
  });
};

export const getVietmapPlaceDetail = async (
  refId: string,
  signal?: AbortSignal
): Promise<VietmapPlaceDetail> => {
  const normalizedRefId = refId.trim();
  if (!normalizedRefId) {
    throw new Error("venue.missingRefId");
  }

  const url = new URL(`${BASE_API_URL}/vietmap/place`);
  url.searchParams.set("ref_id", normalizedRefId);

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      ...getAuthHeader(),
    },
  });
  if (!response.ok) {
    await parseProxyError(response);
  }

  const payload: unknown = await response.json();
  if (!isJsonRecord(payload)) {
    throw new Error("venue.invalidResponse");
  }

  const lat = asNumber(payload.lat);
  const lng = asNumber(payload.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    throw new Error("venue.missingCoordinates");
  }

  return {
    display: asString(payload.display),
    address: asString(payload.address),
    name: asString(payload.name),
    lat,
    lng,
  };
};

export interface VietmapReverseResult {
  display?: string;
  address?: string;
  name?: string;
}

export const reverseGeocodeVietmapCoordinates = async (
  lat: number,
  lng: number,
  signal?: AbortSignal
): Promise<VietmapReverseResult> => {
  const url = new URL(`${BASE_API_URL}/vietmap/reverse`);
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lng", String(lng));

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      ...getAuthHeader(),
    },
  });
  if (!response.ok) {
    await parseProxyError(response);
  }

  const payload: unknown = await response.json();
  if (!isJsonRecord(payload)) {
    return {};
  }

  return {
    display: asString(payload.display) || undefined,
    address: asString(payload.address) || undefined,
    name: asString(payload.name) || undefined,
  };
};
